import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SEO } from '@daycal/ui';
import { api } from '../lib/apiClient.js';
import {
  FiCheck, FiX, FiZap, FiArrowRight, FiVideo, FiCalendar, FiGlobe,
  FiFileText, FiTag, FiShield, FiRefreshCw, FiChevronDown, FiBarChart2,
} from 'react-icons/fi';
import { LuInfinity } from 'react-icons/lu';

const PRICE = '$25';

const INCLUDED = [
  { icon: FiCalendar, title: 'Event types & availability', body: 'Create services, set hours, buffers and date overrides.' },
  { icon: FiGlobe, title: 'Your public booking page', body: 'Share your own link and take bookings in seconds.' },
  { icon: FiFileText, title: 'Custom booking forms', body: 'Ask exactly what you need before each appointment.' },
  { icon: FiTag, title: 'Promo codes & paid events', body: 'Run discounts and charge for consultations.' },
  { icon: FiBarChart2, title: 'Calendar & dashboard', body: 'Manage every booking with live stats and a calendar.' },
  { icon: FiRefreshCw, title: 'All future updates, free', body: 'Every new feature we ship is yours — forever.' },
];

const FAQS = [
  { q: 'Is it really a lifetime license?', a: 'Yes. You pay once and use DayCal for as long as the product exists — no monthly or yearly fees, ever.' },
  { q: "What's included in the deal?", a: 'Everything: your booking page, event types, availability, custom forms, promo codes, paid appointments, the calendar dashboard, built-in video calls, and all future updates.' },
  { q: 'Will I ever be charged again?', a: 'No. The lifetime deal is a single one-time payment. There are no renewals, no hidden fees, and no auto-charges.' },
  { q: 'Is the deal refundable?', a: 'No — the lifetime deal is a one-time, non-refundable purchase. Have questions? Reach out before you buy and we will help.' },
];

function Logo() {
  return (
    <Link to="/" className="group flex items-center">
      <img src="/Day cal logo name.png" alt="DayCal Logo" className="h-16 w-auto -ml-3 -my-3 object-contain transition-transform duration-500 group-hover:scale-105" />
    </Link>
  );
}

// Reveal children on first scroll into view (IntersectionObserver, reveal-once).
// Respects prefers-reduced-motion by showing immediately; a CSS transition does the motion.
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setShown(true); return; }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: shown ? `${delay}ms` : '0ms' }}
      className={`transition-all duration-700 ease-out ${shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </div>
  );
}

function TrustPill({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/70 px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-xs">
      <span className="grid h-4 w-4 place-items-center rounded-full bg-emerald-100 text-emerald-600">
        <FiCheck className="h-2.5 w-2.5" strokeWidth={3} />
      </span>
      {children}
    </span>
  );
}

function Step({ n }) {
  return <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">{n}</span>;
}

function PaymentModal({ open, onClose, loaded, enabled, price, deal }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-[1.75rem] bg-white p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-xl font-bold text-slate-900">Get lifetime access</h3>
            <p className="text-sm text-slate-500">One-time payment · {price}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {!loaded ? (
          <p className="py-10 text-center text-sm text-slate-500">Loading…</p>
        ) : !enabled ? (
          <p className="py-10 text-center text-sm text-slate-600">This lifetime deal isn't available right now. Please check back soon.</p>
        ) : (
          <div className="mt-5">
            {deal.qr_url ? (
              <img src={deal.qr_url} alt="Payment QR" className="mx-auto h-48 w-48 rounded-xl border border-slate-200 object-contain" />
            ) : (
              <div className="mx-auto grid h-48 w-48 place-items-center rounded-xl border border-dashed border-slate-300 text-center text-xs text-slate-400">QR coming soon</div>
            )}
            {deal.upi_id && (
              <p className="mt-3 text-center text-sm text-slate-600">UPI: <span className="font-semibold text-slate-900">{deal.upi_id}</span></p>
            )}
            <ol className="mt-5 space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3"><Step n={1} /> <span>Scan the QR and pay <strong>{price}</strong> via any UPI app.</span></li>
              <li className="flex items-start gap-3"><Step n={2} /> <span>Create your DayCal account (<Link to="/signup" className="text-brand-600 underline">sign up</Link>).</span></li>
              <li className="flex items-start gap-3"><Step n={3} /> <span>Send your payment screenshot{deal.whatsapp_number ? <> to WhatsApp <strong>{deal.whatsapp_number}</strong></> : ' to us'}. We'll activate lifetime access on your account.</span></li>
            </ol>
            <Link to="/signup" onClick={onClose} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-3.5 text-sm font-bold text-white transition-all hover:bg-slate-800">
              Create my account <FiArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Deal() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const { data: dealData } = useQuery({ queryKey: ['lifetime-deal'], queryFn: api.getLifetimeDeal });
  const deal = dealData?.deal;
  const price = deal?.price || PRICE;
  const enabled = !!deal?.enabled;
  const dealLoaded = !!deal;
  const [payOpen, setPayOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col justify-between">
      <SEO path="/deal" title="Lifetime Deal — DayCal" description={`Get lifetime access to DayCal for a one-time ${price}. Every feature, all your bookings, and built-in video calls — no subscriptions, ever.`} />

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'pt-4 px-4' : 'pt-0'}`}>
        <div className={`mx-auto flex items-center justify-between transition-all duration-300 ${
          isScrolled
            ? 'max-w-4xl rounded-full bg-white/95 backdrop-blur-lg shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-200/50 px-6 py-2.5'
            : 'max-w-6xl bg-white/90 backdrop-blur-md px-5 py-3.5 border-b border-slate-200/80'
        }`}>
          <Logo />
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
            <Link to="/" className="hover:text-slate-900 transition-colors">Home</Link>
            <Link to="/deal" className="text-brand-600 font-semibold">Lifetime Deal</Link>
            <Link to="/contact" className="hover:text-slate-900 transition-colors">Contact</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden sm:inline text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Sign in</Link>
            <button type="button" onClick={() => setPayOpen(true)} className="rounded-dc-sm bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-slate-800">
              Get the deal
            </button>
          </div>
        </div>
      </header>

      {/* Hero + deal card */}
      <section className="relative overflow-hidden border-b border-slate-200/60 pt-32 pb-20">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-brand-50/30 to-slate-100/70" />
        <div className="pointer-events-none absolute inset-0 bg-dot-grid opacity-50" />
        <div className="pointer-events-none absolute left-1/4 -top-10 h-80 w-80 rounded-full bg-brand-300/30 blur-3xl animate-dc-float" />
        <div className="pointer-events-none absolute right-1/4 top-20 h-72 w-72 rounded-full bg-indigo-300/20 blur-3xl animate-dc-pulse-glow" />

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 lg:grid-cols-2">
          {/* Left: pitch */}
          <div className="text-center lg:text-left animate-dc-rise">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-300 bg-white/80 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-brand-700 shadow-sm backdrop-blur">
              <FiZap className="h-3.5 w-3.5 text-amber-500" /> Limited lifetime deal
            </span>
            <h1 className="font-display mt-5 text-5xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-6xl">
              Pay once.<br />
              Schedule{' '}
              <span className="animate-dc-gradient bg-gradient-to-r from-brand-500 via-indigo-500 to-emerald-500 bg-clip-text text-transparent">forever.</span>
            </h1>
            <p className="mt-5 max-w-xl mx-auto lg:mx-0 text-base sm:text-lg text-slate-600 leading-relaxed">
              Own DayCal outright — every feature, all your bookings, and built-in video calls — for a single payment of <strong className="text-slate-900">{price}</strong>. No subscriptions. No renewals. Ever.
            </p>
            {dealLoaded && !enabled && (
              <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3.5 py-1.5 text-xs font-semibold text-amber-800">
                This deal isn't available right now — check back soon.
              </p>
            )}
            <div className="mt-7 flex flex-wrap justify-center gap-2.5 lg:justify-start">
              <TrustPill>No subscription</TrustPill>
              <TrustPill>All future updates</TrustPill>
              <TrustPill>Instant access</TrustPill>
            </div>
          </div>

          {/* Right: deal card */}
          <div className="relative mx-auto w-full max-w-md animate-dc-rise" style={{ animationDelay: '120ms' }}>
            <div className="absolute -inset-1 rounded-[2.2rem] bg-gradient-to-br from-brand-400 via-indigo-400 to-emerald-400 opacity-40 blur-xl animate-dc-pulse-glow" />
            <div className="relative rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_24px_70px_rgb(0,0,0,0.14)] transition-transform duration-500 hover:-translate-y-1">
              {/* ribbon */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="relative inline-flex overflow-hidden rounded-full bg-gradient-to-r from-brand-600 to-indigo-600 px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-md">
                  Launch offer
                  <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-white/40 blur-sm animate-dc-shimmer" />
                </span>
              </div>

              <div className="mt-2 flex items-center gap-2">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-slate-900 text-white">
                  <LuInfinity className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-bold text-slate-900">DayCal Lifetime</p>
                  <p className="text-xs text-slate-500">One-time payment</p>
                </div>
              </div>

              <div className="mt-6 flex items-end gap-3">
                <span className="font-display text-7xl font-extrabold leading-none tracking-tight text-slate-900">{price}</span>
                <div className="mb-2 leading-tight">
                  <span className="block text-sm font-bold text-slate-900">once</span>
                  <span className="block text-xs font-medium text-slate-400 line-through">billed monthly</span>
                </div>
              </div>
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                <FiCheck className="h-3 w-3" strokeWidth={3} /> No renewals — ever
              </p>

              <button
                type="button"
                onClick={() => setPayOpen(true)}
                className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-4 text-sm font-bold text-white shadow-lg shadow-slate-900/20 transition-all hover:bg-slate-800 hover:shadow-xl active:scale-[0.99]"
              >
                Get lifetime access <FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>

              <div className="mt-6 space-y-3 border-t border-slate-100 pt-5">
                {['Every feature included', 'All future updates, free', 'Built-in video calls'].map((f) => (
                  <div key={f} className="flex items-center gap-3 text-sm font-medium text-slate-700">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-600">
                      <FiCheck className="h-3 w-3" strokeWidth={3} />
                    </span>
                    {f}
                  </div>
                ))}
              </div>
              <p className="mt-5 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                <FiShield className="h-3.5 w-3.5" /> One-time lifetime purchase · all sales final
              </p>
            </div>
          </div>
        </div>
      </section>

      <main className="flex-1 w-full">
        {/* Monthly vs Lifetime */}
        <section className="mx-auto max-w-5xl px-5 py-20">
          <Reveal>
            <div className="text-center">
              <h2 className="font-display text-3xl font-bold text-slate-900 sm:text-4xl">Stop renting your scheduler</h2>
              <p className="mt-3 text-slate-600">Most tools bill you every single month. Own DayCal once and be done.</p>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-dc border border-slate-200 bg-white p-8">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">The usual way</p>
                <p className="mt-2 font-display text-2xl font-bold text-slate-900">Monthly subscription</p>
                <ul className="mt-6 space-y-3 text-sm text-slate-500">
                  {['Pay again every single month', 'Renews automatically, forever', 'Prices can go up over time', 'Lose access the moment you stop'].map((c) => (
                    <li key={c} className="flex items-center gap-3">
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-rose-50 text-rose-400">
                        <FiX className="h-3 w-3" strokeWidth={3} />
                      </span>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="relative h-full rounded-dc border-2 border-brand-300 bg-gradient-to-br from-brand-50/60 via-white to-emerald-50/40 p-8 shadow-sm">
                <span className="absolute right-6 top-6 rounded-full bg-brand-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">Best value</span>
                <p className="text-xs font-bold uppercase tracking-wider text-brand-600">The DayCal way</p>
                <p className="mt-2 font-display text-2xl font-bold text-slate-900">Lifetime — {price} once</p>
                <ul className="mt-6 space-y-3 text-sm font-medium text-slate-700">
                  {['One payment, then never again', 'Yours for as long as DayCal exists', 'Locked-in price — no increases', 'Every future update included'].map((c) => (
                    <li key={c} className="flex items-center gap-3">
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-600">
                        <FiCheck className="h-3 w-3" strokeWidth={3} />
                      </span>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Highlight: video calls */}
        <section className="mx-auto max-w-6xl px-5 pb-6">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 p-8 sm:p-12">
              <div className="grid items-center gap-8 lg:grid-cols-2">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white">
                    <FiVideo className="h-3.5 w-3.5" /> Built in — not bolted on
                  </span>
                  <h3 className="font-display mt-4 text-2xl font-bold text-white sm:text-3xl">Built-in video calls</h3>
                  <p className="mt-3 text-slate-300 leading-relaxed">
                    Every online booking comes with a ready-to-join meeting room — no Google Meet, no Zoom, no extra apps or per-seat fees. It is part of the lifetime deal.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: FiVideo, label: 'Instant rooms' },
                    { icon: FiZap, label: 'No apps to install' },
                    { icon: LuInfinity, label: 'Unlimited calls' },
                    { icon: FiShield, label: 'No third parties' },
                  ].map(({ icon: Icon, label }) => (
                    <div key={label} className="rounded-dc-sm border border-white/10 bg-white/5 p-4 text-center transition-colors hover:bg-white/10">
                      <Icon className="mx-auto h-6 w-6 text-brand-300" />
                      <p className="mt-2 text-sm font-semibold text-white">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </section>

        {/* Feature grid */}
        <section className="mx-auto max-w-6xl px-5 py-16">
          <Reveal>
            <div className="text-center">
              <h2 className="font-display text-3xl font-bold text-slate-900">Everything, for one price</h2>
              <p className="mt-3 text-slate-600">One payment unlocks the whole platform — and keeps unlocking it as we grow.</p>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {INCLUDED.map(({ icon: Icon, title, body }, i) => (
              <Reveal key={title} delay={(i % 3) * 90}>
                <div className="group h-full rounded-dc border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-brand-200 hover:shadow-md">
                  <div className="grid h-11 w-11 place-items-center rounded-dc-sm bg-brand-50 text-brand-600 transition-colors group-hover:bg-brand-600 group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-bold text-slate-900">{title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-3xl px-5 pb-16">
          <Reveal>
            <h2 className="font-display text-center text-3xl font-bold text-slate-900">Questions, answered</h2>
            <div className="mt-8 space-y-4">
              {FAQS.map((faq) => (
                <details key={faq.q} className="group rounded-dc border border-slate-200 bg-white [&_summary::-webkit-details-marker]:hidden">
                  <summary className="flex cursor-pointer items-center justify-between gap-2 p-5 font-semibold text-slate-900">
                    <span>{faq.q}</span>
                    <FiChevronDown className="h-5 w-5 shrink-0 text-slate-400 transition-transform duration-300 group-open:-rotate-180" />
                  </summary>
                  <div className="border-t border-slate-100 px-5 pb-5 pt-4 text-sm leading-relaxed text-slate-600">{faq.a}</div>
                </details>
              ))}
            </div>
          </Reveal>
        </section>

        {/* Final CTA */}
        <section className="mx-auto max-w-6xl px-5 pb-20">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] border border-brand-200 bg-gradient-to-br from-brand-600 via-indigo-600 to-brand-700 p-12 text-center shadow-xl">
              <div className="pointer-events-none absolute inset-0 bg-dot-grid opacity-20" />
              <div className="relative">
                <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">Lock in lifetime access for {price}</h2>
                <p className="mx-auto mt-3 max-w-xl text-brand-50/90">Pay once today and never see a renewal. Your scheduling, your video calls, your platform — for good.</p>
                <button
                  type="button"
                  onClick={() => setPayOpen(true)}
                  className="group mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-brand-700 shadow-lg transition-all hover:bg-brand-50 active:scale-[0.99]"
                >
                  Get lifetime access <FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <p className="mt-4 text-xs text-brand-50/70">One-time payment · No subscription · Lifetime access</p>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} loaded={dealLoaded} enabled={enabled} price={price} deal={deal} />

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-xs text-slate-500 sm:flex-row">
          <Logo />
          <p>© {new Date().getFullYear()} DayCal. All rights reserved.</p>
          <div className="flex flex-wrap gap-6 justify-center">
            <Link to="/deal" className="text-brand-600 font-bold">Lifetime Deal</Link>
            <Link to="/contact" className="hover:text-slate-900 transition-colors">Contact Us</Link>
            <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-900 transition-colors">Terms of Service</Link>
            <Link to="/refund" className="hover:text-slate-900 transition-colors">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
